import {
  alpha,
  Box,
  Button,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Perfil } from "../../../pages/easyReport/perfilRelatorio/Index";
import { useStyle } from "./styles";
import { v4 as uuidv4 } from "uuid";
import {
  AddOutlined,
  CloseRounded,
  DeleteOutline,
  TuneOutlined,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { notifyOnPage } from "../../../utils/notifyOnPage";

export const NewPerfilModal = ({
  setPerfis,
  setModalOpen,
  perfis,
}: {
  setPerfis: React.Dispatch<React.SetStateAction<Perfil[]>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  perfis: Perfil[];
}) => {
  const theme = useTheme();
  const styles = useStyle(theme);
  const navigate = useNavigate();

  const [newPerfil, setNewPerfil] = useState<Perfil>({
    id: uuidv4(),
    titulo: "",
    descricao: "",
    funcoes: [
      {
        idFuncao: "",
        nomeFuncao: "",
        view: "",
        code: "",
        styles: "",
        variaveis: [],
      },
    ],
  });
  const [funcoes, setFuncoes] = useState<
    | {
        idFuncao: string;
        nomeFuncao: string;
      }[]
    | null
  >(null);
  const [funcaoLoad, setFuncaoLoad] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchFuncoes = async () => {
      try {
        setFuncaoLoad(true);
        const response = await api.get("/easyReport/v2/findManyFunctions");
        setFuncoes(
          response.data.map((f: any) => ({
            idFuncao: f.id,
            nomeFuncao: f.titulo,
          })),
        );
      } catch (error: any) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios",
          error?.response?.data?.error || "Erro ao carregar funções",
          "error",
          3000,
        );
      } finally {
        setFuncaoLoad(false);
      }
    };
    fetchFuncoes();
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const logsString = localStorage.getItem("logs");
      const logsLoginClienteId = logsString ? JSON.parse(logsString)?.id : "";
      const response = await api.post("/easyReport/v2/createProfile", {
        titulo: newPerfil.titulo,
        descricao: newPerfil.descricao,
        funcoes: newPerfil.funcoes.map((f) => f.idFuncao),
        logsLoginClienteId,
      });
      setPerfis((prev) => [...prev, response.data]);
      setModalOpen(false);
      notifyOnPage(
        "easyReportV2/perfilRelatorios",
        "Perfil criado com sucesso",
        "success",
        3000,
      );
    } catch (error: any) {
      notifyOnPage(
        "easyReportV2/perfilRelatorios",
        error?.response?.data?.error || "Erro ao salvar perfil",
        "error",
        3000,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddFuncao = () => {
    const selectedIds = newPerfil.funcoes.map((f) => f.idFuncao);
    if (!funcoes) return;
    const availableOptions = funcoes.filter(
      (f) => !selectedIds.includes(f.idFuncao),
    );
    if (availableOptions.length === 0) return;
    setNewPerfil({
      ...newPerfil,
      funcoes: [
        ...(newPerfil.funcoes || []),
        {
          ...availableOptions[0],
          view: "",
          code: "",
          styles: "",
          variaveis: [],
        },
      ],
    });
  };

  const handleSelectChange = (index: number, value: string) => {
    const newFuncoes = [...newPerfil.funcoes];
    const selectedFuncao = funcoes?.find((f) => f.idFuncao === value);
    if (!selectedFuncao) return;
    newFuncoes[index] = {
      ...selectedFuncao,
      view: "",
      code: "",
      styles: "",
      variaveis: [],
    };
    setNewPerfil((prev) => (prev ? { ...prev, funcoes: newFuncoes } : prev));
  };

  const handleDeleteFuncao = (index: number) => {
    const newFuncoes = [...(newPerfil.funcoes || [])];
    newFuncoes.splice(index, 1);
    setNewPerfil({
      ...newPerfil,
      funcoes: newFuncoes,
    });
  };

  const verifyField = () => {
    if (
      newPerfil.descricao &&
      newPerfil.titulo &&
      newPerfil.funcoes[0].idFuncao
    ) {
      return false;
    } else {
      return true;
    }
  };

  const isTituloDuplicated = perfis.some(
    (p) =>
      p.titulo.trim().toLowerCase() === newPerfil.titulo.trim().toLowerCase() &&
      p.id !== newPerfil.id,
  );

  return (
    <Paper sx={styles.paper}>
      <Box sx={styles.modalContainer}>
        <Box sx={styles.headerContainer}>
          <Stack
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              width: "100%",
              height: "100%",
            }}
          >
            <Typography variant="h6">Novo Perfil</Typography>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Adicione novos perfis de relatório
            </Typography>
          </Stack>

          <IconButton aria-label="Fechar" onClick={(e) => setModalOpen(false)}>
            <CloseRounded />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1 }} />
        <Box height={"100%"}>
          <Box sx={styles.TextField}>
            <Typography variant="subtitle2">Adicione um título</Typography>
            <TextField
              fullWidth
              variant="outlined"
              slotProps={{
                input: {
                  sx: {
                    color: theme.palette.text.secondary,
                  },
                },
              }}
              size="small"
              value={newPerfil.titulo}
              onChange={(e) =>
                setNewPerfil((prev) =>
                  prev ? { ...prev, titulo: e.target.value } : prev,
                )
              }
              placeholder="Digite o título"
              error={isTituloDuplicated}
              helperText={
                isTituloDuplicated ? "Título já existe. Escolha outro." : ""
              }
            />
            <Typography variant="subtitle2">Descrição</Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={newPerfil.descricao}
              onChange={(e) =>
                setNewPerfil((prev) =>
                  prev ? { ...prev, descricao: e.target.value } : prev,
                )
              }
              placeholder="Digite a descrição"
              multiline
              minRows={4}
              slotProps={{
                input: {
                  sx: {
                    color: theme.palette.text.secondary,
                  },
                },
              }}
            />
            <Box sx={styles.selectContainer}>
              <Typography variant="subtitle2">Selecione a função</Typography>
              <Button
                onClick={() =>
                  navigate("/easyReportV2/perfilRelatorios/novaFuncao")
                }
                startIcon={<TuneOutlined />}
                variant="outlined"
              >
                Criar Função
              </Button>
            </Box>
            {newPerfil.funcoes.map((func, index) => {
              const selectedIds = newPerfil.funcoes
                .filter((_, i) => i !== index)
                .map((f) => f.idFuncao);
              const availableOptions = funcoes?.filter(
                (f) => !selectedIds.includes(f.idFuncao),
              );
              return (
                <Box sx={{ display: "flex" }} key={func.idFuncao}>
                  <Box sx={{ width: "100%" }}>
                    <Select
                      value={func.idFuncao}
                      fullWidth
                      size="small"
                      key={`${func.idFuncao}-Select`}
                      onChange={(e) =>
                        handleSelectChange(index, e.target.value)
                      }
                      inputProps={{ "aria-label": "Função" }}
                      sx={{
                        borderRadius: "8px",
                        boxShadow: "0px 0px 3px #0000001a",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "transparent",
                        },
                      }}
                    >
                      {availableOptions?.map((f) => (
                        <MenuItem value={f.idFuncao} key={f.idFuncao}>
                          {f.nomeFuncao}
                        </MenuItem>
                      ))}
                    </Select>
                    <Fade in={funcaoLoad} unmountOnExit>
                      <LinearProgress />
                    </Fade>
                  </Box>
                  <IconButton
                    aria-label="Remover função"
                    onClick={() => handleDeleteFuncao(index)}
                    disabled={!(newPerfil.funcoes.length > 1)}
                    color="error"
                  >
                    <DeleteOutline />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ mt: 1 }}>
            <Button onClick={handleAddFuncao}>
              <AddOutlined /> Adicionar Função
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              gap: 2,
              paddingY: 2,
            }}
          >
            <Button
              sx={styles.cancelButton}
              variant="contained"
              onClick={(e) => setModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={verifyField() || isTituloDuplicated || saving}
              variant="contained"
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
